import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { submitToZoho } from '@/utils/secureFormSubmission';

interface ZohoFormWrapperProps {
  title?: string;
  buttonText?: string;
  className?: string;
}

const ZohoFormWrapper: React.FC<ZohoFormWrapperProps> = ({ 
  title = "Get in Touch", 
  buttonText = "Submit",
  className = ""
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim() || 
        !formData.email.trim() || !formData.company.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!privacyConsent) {
      toast.error('Please accept the privacy policy to continue.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const success = await submitToZoho({
        ...formData,
        formType: "General Contact Form"
      });
      
      if (success) {
        toast.success('Thank you! We\'ll be in touch soon.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          message: ''
        });
        setPrivacyConsent(false);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg p-6 shadow-sm border ${className}`}>
      <h3 className="text-xl font-semibold mb-6 text-gray-900">{title}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <Input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              required
              disabled={isSubmitting}
              maxLength={40}
              autoComplete="given-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <Input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              required
              disabled={isSubmitting}
              maxLength={80}
              autoComplete="family-name"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Work Email <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john.doe@company.com"
            required
            disabled={isSubmitting}
            maxLength={100}
            autoComplete="email"
            pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <Input
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Acme Inc."
            required
            disabled={isSubmitting}
            maxLength={200}
            autoComplete="organization"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            How can we help?
          </label>
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your security challenges..."
            rows={3}
            disabled={isSubmitting}
            maxLength={2000}
          />
        </div>
        
        <div className="flex items-start space-x-2">
          <Checkbox 
            id="privacyConsent"
            checked={privacyConsent}
            onCheckedChange={(checked) => setPrivacyConsent(checked as boolean)}
            disabled={isSubmitting}
          />
          <label htmlFor="privacyConsent" className="text-sm text-gray-600 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            I agree to the{" "}
            <a href="/privacy-policy" target="_blank" className="text-[#8932c8] hover:underline">
              Privacy Policy
            </a>{" "}
            and consent to the processing of my personal data. <span className="text-red-500">*</span>
          </label>
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-[#8932c8] hover:bg-[#7a2cb3]"
          disabled={isSubmitting || !privacyConsent}
        >
          {isSubmitting ? "Submitting..." : buttonText}
        </Button>
      </form>
    </div>
  );
};

export default ZohoFormWrapper;